/** 正文设备链接汇总为底部「相关链接」小卡片（门户） */
import {
  extractProductIdFromHref,
  findPortalCategory,
  parseProductLinkLabel,
  resolveProductLinkTarget,
} from '@/components/modules/products/utils/productHelpers'
import { resolveFileUrl } from '@/components/shared/utils/fileUrl'
import { getPortalCtaMessages } from '@/components/portal/messages/portalCtaMessages'

const SECTION_ATTR = 'data-related-links-section'
const CARD_ATTR = 'data-product-link-card'
const PRODUCT_CATEGORY_PREFIX = '跳转至分类：'
const PRODUCT_LINK_PREFIX = '跳转至产品：'
const JUMP_LABEL_RE = /跳转至\s*(产品|分类)\s*[：:]\s*(.+)$/
const JUMP_LABEL_GLOBAL_RE = /跳转至\s*(产品|分类)\s*[：:]\s*([^\n\r<]+)/g
/** 正文底部「相关链接」卡片；改为 false 可暂时关闭 */
const RELATED_LINKS_ENABLED = true

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function normalizeJumpLabel(label) {
  return String(label || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** 从分类链接文案解析名称 */
export function parseProductCategoryLinkLabel(label) {
  const text = normalizeJumpLabel(label)
  const match = text.match(JUMP_LABEL_RE)
  if (match?.[1] === '分类') return String(match[2] || '').trim()
  if (!text.startsWith(PRODUCT_CATEGORY_PREFIX)) return ''
  return text.slice(PRODUCT_CATEGORY_PREFIX.length).trim()
}

/** 是否为设备（产品/分类）跳转链接 */
export function isProductDeviceAnchor(link) {
  if (!link || link.closest?.(`[${SECTION_ATTR}]`)) return false
  // 图文热点内设备卡片单独渲染，不参与正文底部相关链接汇总
  if (link.closest?.('.hotspot-tabs, .hotspot-device-card')) return false
  if (String(link.getAttribute?.('data-related-card') || '').trim() === '0') return false
  const href = link.getAttribute?.('href') || ''
  const type = link.getAttribute?.('data-link-type') || ''
  const text = normalizeJumpLabel(link.textContent || '')
  return type === 'product'
    || type === 'product-category'
    || /\/products\/(detail|category)\//.test(href)
    || JUMP_LABEL_RE.test(text)
    || text.startsWith(PRODUCT_LINK_PREFIX)
    || text.startsWith(PRODUCT_CATEGORY_PREFIX)
}

/** 构建设备/分类统一卡片 HTML */
const LEARN_MORE_ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>'

function isCategoryLinkCard(card = {}) {
  const type = String(card.linkType || '').trim()
  if (type === 'product-category') return true
  if (type === 'product') return false
  return Boolean(card.categoryId) && !card.productId
}

function getDeviceLinkCtaMessages(locale) {
  const messages = getPortalCtaMessages(locale)
  return {
    learnMore: messages.learnMore,
    relatedLinksTitle: messages.relatedLinksTitle || '查看相关产品',
  }
}

export function buildProductLinkCardHtml(card = {}, options = {}) {
  const isCategory = isCategoryLinkCard(card)
  const compact = options.compact === true
  const href = escapeHtml(card.href || '#')
  const title = escapeHtml(String(card.title || '').trim() || (isCategory ? '产品分类' : '产品'))
  const image = resolveFileUrl(card.image || '')
  const productId = !isCategory && card.productId ? ` data-product-id="${escapeHtml(card.productId)}"` : ''
  const categoryId = card.categoryId ? ` data-category-id="${escapeHtml(card.categoryId)}"` : ''
  const linkType = card.linkType ? ` data-link-type="${escapeHtml(card.linkType)}"` : ''
  const cta = getDeviceLinkCtaMessages(options.locale)
  const learnMore = escapeHtml(options.learnMoreLabel || cta.learnMore)
  const kindClass = isCategory ? 'rich-body-device-link-card--category' : 'rich-body-device-link-card--product'
  const className = [
    'rich-body-device-link-card',
    kindClass,
    compact ? 'rich-body-device-link-card--compact' : '',
    image ? '' : 'rich-body-device-link-card--empty',
  ].filter(Boolean).join(' ')
  const imageHtml = image
    ? `<img class="rich-body-device-link-card__image" src="${escapeHtml(image)}" alt="" aria-hidden="true">`
    : `<div class="rich-body-device-link-card__image" aria-hidden="true"></div>`
  const certsHtml = isCategory ? '' : buildDeviceLinkCertsHtml(card.certifications)

  return (
    `<article class="${className}" data-href="${href}"${linkType}${productId}${categoryId}>`
    + `<div class="rich-body-device-link-card__media">`
    + imageHtml
    + `</div>`
    + `<div class="rich-body-device-link-card__body">`
    + `<h3 class="rich-body-device-link-card__title">${title}</h3>`
    + certsHtml
    + `<a class="rich-body-device-link-card__cta" href="${href}"><span>${learnMore}</span>${LEARN_MORE_ARROW}</a>`
    + `</div>`
    + '</article>'
  )
}

function normalizeDeviceCertUrls(list) {
  if (!Array.isArray(list)) return []
  const urls = []
  const seen = new Set()
  for (const entry of list) {
    const url = typeof entry === 'string'
      ? entry.trim()
      : String(entry?.url || entry?.fileUrl || entry?.iconUrl || entry?.thumbnailUrl || '').trim()
    if (!url || seen.has(url)) continue
    seen.add(url)
    urls.push(url)
  }
  return urls
}

function buildDeviceLinkCertsHtml(list) {
  const urls = normalizeDeviceCertUrls(list)
  if (!urls.length) return ''
  const items = urls
    .slice(0, 9)
    .map((url) => `<div class="rich-body-device-link-card__cert"><img src="${escapeHtml(resolveFileUrl(url))}" alt="" loading="lazy" onerror="this.parentNode&&this.parentNode.remove()"></div>`)
    .join('')
  return `<div class="rich-body-device-link-card__certs">${items}</div>`
}

function parseCardCertsAttr(value) {
  const raw = String(value || '').trim()
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return normalizeDeviceCertUrls(Array.isArray(parsed) ? parsed : [parsed])
  } catch {
    return raw.split('|').map((item) => item.trim()).filter(Boolean)
  }
}

function refreshCertNavState(wrap) {
  const track = wrap?.querySelector?.('.rich-body-product-card__certs')
  if (!track) return
  const overflow = track.scrollWidth > track.clientWidth + 1
  const maxScroll = track.scrollWidth - track.clientWidth
  const atStart = track.scrollLeft <= 1
  const atEnd = !overflow || track.scrollLeft >= maxScroll - 1
  const prev = wrap.querySelector('.rich-body-product-card__certs-nav--prev')
  const next = wrap.querySelector('.rich-body-product-card__certs-nav--next')
  if (prev) {
    prev.classList.toggle('is-disabled', atStart)
    prev.setAttribute('aria-disabled', atStart ? 'true' : 'false')
  }
  if (next) {
    next.classList.toggle('is-disabled', atEnd)
    next.setAttribute('aria-disabled', atEnd ? 'true' : 'false')
  }
}

function scrollRelatedCerts(track, direction) {
  if (!track) return
  const cert = track.querySelector('.rich-body-product-card__cert')
  const styles = getComputedStyle(track)
  const gap = Number.parseFloat(styles.columnGap || styles.gap || 0) || 8
  const step = cert ? cert.offsetWidth + gap : track.clientWidth / 3
  track.scrollBy({ left: direction * step, behavior: 'smooth' })
}

function onRelatedCertNavClick(event) {
  const nav = event.target.closest?.('[data-cert-nav]')
  if (!nav) return
  event.preventDefault()
  event.stopPropagation()
  if (nav.classList.contains('is-disabled') || nav.getAttribute('aria-disabled') === 'true') return
  const wrap = nav.closest('.rich-body-product-card__certs-wrap')
  const track = wrap?.querySelector('.rich-body-product-card__certs')
  const direction = Number(nav.getAttribute('data-cert-nav')) || 0
  scrollRelatedCerts(track, direction)
}

function onRelatedCertNavKeydown(event) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  const nav = event.target.closest?.('[data-cert-nav]')
  if (!nav) return
  event.preventDefault()
  event.stopPropagation()
  nav.click()
}

function bindCertsWrap(wrap) {
  const track = wrap.querySelector('.rich-body-product-card__certs')
  if (!track) return
  const refresh = () => refreshCertNavState(wrap)
  if (track.dataset.certNavScrollBound !== '1') {
    track.dataset.certNavScrollBound = '1'
    track.addEventListener('scroll', refresh, { passive: true })
  }
  wrap.querySelectorAll('img').forEach((img) => {
    if (!img.complete) img.addEventListener('load', refresh)
  })
  refresh()
  requestAnimationFrame(refresh)
}

function unbindRelatedLinkCertNav(root) {
  root?.querySelectorAll?.('.rich-body-product-card__certs').forEach((track) => {
    delete track.dataset.certNavScrollBound
  })
}

function bindRelatedLinkCertNav(root) {
  if (!root) return
  if (root.dataset.relatedCertNavBound !== '1') {
    root.dataset.relatedCertNavBound = '1'
    root.addEventListener('click', onRelatedCertNavClick, true)
    root.addEventListener('keydown', onRelatedCertNavKeydown)
  }
  root.querySelectorAll('.rich-body-product-card__certs-wrap').forEach(bindCertsWrap)
}

/** 在分类树中按名称查找 */
function findCategoryByName(categories, name) {
  const target = String(name || '').trim()
  if (!target) return null
  const walk = (nodes = []) => {
    for (const node of nodes) {
      if (String(node.name || '').trim() === target) return node
      const found = walk(node.children || [])
      if (found) return found
    }
    return null
  }
  return walk(categories)
}

function pickPortalCategoryOwnImage(category) {
  if (!category) return ''
  return String(category.backgroundUrl || category.iconUrl || category.categoryIcon || '').trim()
}

function resolvePortalCategoryImage(category, categories = [], storedImage = '') {
  const own = pickPortalCategoryOwnImage(category)
  if (own) return own
  let current = category
  const seen = new Set()
  while (current) {
    const parentId = current.parentId
    if (parentId == null || parentId === '') break
    const key = String(current.id ?? '')
    if (key && seen.has(key)) break
    if (key) seen.add(key)
    current = findPortalCategory(categories, parentId)
    const url = pickPortalCategoryOwnImage(current)
    if (url) return url
  }
  return String(storedImage || '').trim()
}
export function resolveCardFromLink(link, { products = [], categories = [] } = {}) {
  if (!link) return null
  const href = link.getAttribute('href') || ''
  const label = String(link.textContent || '').trim()
  const linkType = link.getAttribute('data-link-type') || ''
  const textIsCategory = label.startsWith(PRODUCT_CATEGORY_PREFIX)
  const textIsProduct = label.startsWith(PRODUCT_LINK_PREFIX)
  const isCategory = linkType === 'product-category'
    || /\/products\/category\//.test(href)
    || textIsCategory
  const isProduct = linkType === 'product'
    || /\/products\/detail\//.test(href)
    || textIsProduct
  if (!isCategory && !isProduct) return null

  const cardTitle = link.getAttribute('data-card-title') || ''
  const cardImage = link.getAttribute('data-card-image') || ''
  const cardSubtitle = link.getAttribute('data-card-subtitle') || ''

  if (isCategory) {
    let categoryId = link.getAttribute('data-category-id')
      || (href.match(/\/products\/category\/([^/?#]+)/) || [])[1]
      || ''
    const nameFromLabel = parseProductCategoryLinkLabel(label)
    let category = findPortalCategory(categories, categoryId)
    if (!category && nameFromLabel) {
      category = findCategoryByName(categories, nameFromLabel)
      if (category?.id) categoryId = String(category.id)
    }
    const title = cardTitle || category?.name || nameFromLabel || '产品分类'
    const image = resolvePortalCategoryImage(category, categories, cardImage)
    const parent = category?.parentId != null
      ? findPortalCategory(categories, category.parentId)
      : null
    const subtitle = cardSubtitle || parent?.name || ''
    return {
      href: href && href !== '#'
        ? href
        : (categoryId ? `/products/category/${categoryId}` : '/products'),
      title,
      image,
      subtitle,
      categoryId,
      linkType: 'product-category',
      dedupeKey: `category:${categoryId || title}`,
    }
  }

  const productId = link.getAttribute('data-product-id')
    || extractProductIdFromHref(href)
    || ''
  const product = resolveProductLinkTarget(products, {
    id: productId,
    label,
    title: cardTitle,
    coverUrl: cardImage,
    categoryId: link.getAttribute('data-category-id') || '',
  })
  const title = cardTitle
    || product?.title
    || parseProductLinkLabel(label)
    || label
    || '产品'
  const image = product?.image || product?.coverUrl || cardImage || ''
  const subtitle = cardSubtitle || product?.categoryName || ''
  const storedCerts = parseCardCertsAttr(link.getAttribute('data-card-certs'))
  const certifications = Array.isArray(product?.certifications) && product.certifications.length
    ? product.certifications
    : storedCerts
  return {
    href: product?.id
      ? `/products/detail/${product.id}`
      : (href && href !== '#' ? href : '/products'),
    title,
    image,
    subtitle,
    productId: product?.id || productId,
    linkType: 'product',
    certifications,
    dedupeKey: `product:${product?.id || productId || title}`,
  }
}

function collectCardsFromPlainText(html, options = {}) {
  const text = String(html || '')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, '\n')
    .replace(/&nbsp;/gi, ' ')
  const cards = []
  const seen = new Set()
  JUMP_LABEL_GLOBAL_RE.lastIndex = 0
  let match = JUMP_LABEL_GLOBAL_RE.exec(text)
  while (match) {
    const isCategory = match[1] === '分类'
    const name = String(match[2] || '').trim()
    if (!name) {
      match = JUMP_LABEL_GLOBAL_RE.exec(text)
      continue
    }
    let card
    if (isCategory) {
      let category = findCategoryByName(options.categories || [], name)
      const categoryId = category?.id ? String(category.id) : ''
      const parent = category?.parentId != null
        ? findPortalCategory(options.categories || [], category.parentId)
        : null
      card = {
        href: categoryId ? `/products/category/${categoryId}` : '/products',
        title: category?.name || name,
        image: resolvePortalCategoryImage(category, options.categories || []),
        subtitle: parent?.name || '',
        categoryId,
        linkType: 'product-category',
        dedupeKey: `category:${categoryId || name}`,
      }
    } else {
      const product = resolveProductLinkTarget(options.products || [], {
        label: `${PRODUCT_LINK_PREFIX}${name}`,
        title: name,
      })
      card = {
        href: product?.id ? `/products/detail/${product.id}` : '/products',
        title: product?.title || name,
        image: product?.image || product?.coverUrl || '',
        subtitle: product?.categoryName || '',
        productId: product?.id || '',
        linkType: 'product',
        certifications: product?.certifications || [],
        dedupeKey: `product:${product?.id || name}`,
      }
    }
    if (card?.title && !seen.has(card.dedupeKey)) {
      seen.add(card.dedupeKey)
      cards.push(card)
    }
    match = JUMP_LABEL_GLOBAL_RE.exec(text)
  }
  return cards
}

/**
 * 收集正文中的设备链接，在底部注入「相关链接」卡片区
 * @param {HTMLElement} root
 * @param {{ products?: Array, categories?: Array }} [options]
 */
export function injectProductLinkCards(root, options = {}) {
  if (!root || typeof root.querySelectorAll !== 'function') return

  unbindRelatedLinkCertNav(root)
  root.querySelectorAll(`[${SECTION_ATTR}], .rich-body-related-links`).forEach((node) => node.remove())
  root.querySelectorAll(`[${CARD_ATTR}]`).forEach((node) => node.remove())

  if (!RELATED_LINKS_ENABLED) return

  const links = Array.from(root.querySelectorAll('a')).filter(isProductDeviceAnchor)
  const cards = []
  const seen = new Set()

  for (const link of links) {
    const card = resolveCardFromLink(link, options)
    if (!card?.title) continue
    const key = card.dedupeKey || `${card.linkType}:${card.href}:${card.title}`
    if (seen.has(key)) continue
    seen.add(key)
    cards.push(card)
  }

  if (!cards.length) {
    collectCardsFromPlainText(root.innerHTML || '', options).forEach((card) => {
      if (!seen.has(card.dedupeKey)) {
        seen.add(card.dedupeKey)
        cards.push(card)
      }
    })
  }

  if (!cards.length) return

  const section = document.createElement('section')
  section.setAttribute(SECTION_ATTR, '1')
  section.className = 'rich-body-related-links'
  const grid = cards
    .map((card) => (
      `<div class="rich-body-product-card-wrap" ${CARD_ATTR}="1">${buildProductLinkCardHtml(card, options)}</div>`
    ))
    .join('')
  const title = escapeHtml(getDeviceLinkCtaMessages(options.locale).relatedLinksTitle)
  section.innerHTML = (
    `<h2 class="rich-body-related-links__title">${title}</h2>`
    + `<div class="rich-body-related-links__grid">${grid}</div>`
  )
  root.appendChild(section)
  bindRelatedLinkCertNav(root)
}

function extractHotspotCardImage(card) {
  const cover = card.querySelector('.rich-body-device-link-card__image, .rich-body-category-portal-card__bg')
  const src = cover?.getAttribute?.('src') || ''
  if (src) return src
  const style = cover?.getAttribute('style') || cover?.style?.backgroundImage || ''
  const match = String(style).match(/url\((['"]?)([^'")]+)\1\)/)
  if (match?.[2]) return match[2]
  return card.querySelector('.rich-body-product-card__image')?.getAttribute('src') || ''
}

function extractHotspotCardTitle(card) {
  return String(
    card.querySelector('.rich-body-device-link-card__title, .rich-body-product-card__name, .rich-body-category-portal-card__title')?.textContent || '',
  ).trim()
}

function extractHotspotCardCerts(card) {
  return Array.from(card.querySelectorAll?.('.rich-body-device-link-card__cert img') || [])
    .map((img) => img.getAttribute('src') || '')
    .filter(Boolean)
}

function isHotspotCategoryCard(card) {
  const linkType = card.getAttribute('data-link-type') || ''
  const categoryId = card.getAttribute('data-category-id') || ''
  const productId = card.getAttribute('data-product-id') || ''
  if (linkType === 'product-category') return true
  if (card.classList.contains('rich-body-device-link-card--category')) return true
  if (card.classList.contains('rich-body-category-portal-card')) return true
  return Boolean(categoryId) && !productId
}

function rebuildHotspotCardFromIndex(card, options = {}) {
  const { products = [], categories = [], locale } = options
  const categoryId = card.getAttribute('data-category-id') || ''
  const productId = card.getAttribute('data-product-id') || ''
  const href = card.getAttribute('data-href')
    || card.querySelector('a[href]')?.getAttribute('href')
    || '#'
  const storedImage = extractHotspotCardImage(card)
  const storedTitle = extractHotspotCardTitle(card)
  const storedCerts = extractHotspotCardCerts(card)

  if (isHotspotCategoryCard(card)) {
    const category = findPortalCategory(categories, categoryId)
    return buildProductLinkCardHtml({
      href: href && href !== '#'
        ? href
        : (categoryId ? `/products/category/${categoryId}` : '#'),
      title: category?.name || storedTitle || '产品分类',
      image: resolvePortalCategoryImage(category, categories, storedImage),
      linkType: 'product-category',
      categoryId,
    }, { compact: true, locale })
  }

  const product = resolveProductLinkTarget(products, {
    id: productId,
    title: storedTitle,
    coverUrl: storedImage,
    categoryId,
  })
  const resolvedCerts = Array.isArray(product?.certifications) && product.certifications.length
    ? product.certifications
    : storedCerts
  return buildProductLinkCardHtml({
    href: product?.id
      ? `/products/detail/${product.id}`
      : href,
    title: product?.title || storedTitle || '产品',
    image: product?.image || product?.coverUrl || storedImage || '',
    productId: product?.id || productId,
    categoryId,
    linkType: 'product',
    certifications: resolvedCerts,
  }, { compact: true, locale })
}

/**
 * 按产品/分类索引整卡重写图文热点设备卡（封面+认证与底部相关链接同源）
 * @param {HTMLElement} root
 * @param {{ products?: Array, categories?: Array, locale?: string }} [options]
 */
export function enhanceHotspotDeviceCards(root, options = {}) {
  if (!root || typeof root.querySelectorAll !== 'function') return

  root.querySelectorAll([
    '.hotspot-device-card .rich-body-product-card',
    '.hotspot-device-card .rich-body-category-portal-card',
    '.hotspot-device-card .rich-body-device-link-card',
  ].join(', ')).forEach((card) => {
    card.outerHTML = rebuildHotspotCardFromIndex(card, options)
  })

  bindRelatedLinkCertNav(root)
}
